import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Radio, Checkbox, TextField } from '@p81-common/ui';
import { ACTIONS } from '../helpers/config';
import createClasses from './styles';

type VersionType = 'stable' | 'latest' | 'custom';

interface VersionSelectionModalProps {
  action: ACTIONS | null;
  open: boolean;
  initialVersion?: 'stable' | 'latest';
  onClose: () => void;
  onProceed: (version: string, gradual?: boolean) => void;
}

const isValidCustomVersion = (value: string): boolean => /^\d{10}$/.test(value);

const VersionSelectionModal = ({
  action,
  open,
  initialVersion = 'stable',
  onClose,
  onProceed
}: VersionSelectionModalProps) => {
  const { t } = useTranslation(['activity']);
  const [version, setVersion] = useState<VersionType>(initialVersion);
  const [customVersion, setCustomVersion] = useState<string>('');
  const [gradual, setGradual] = useState<boolean>(false);
  const classes = createClasses();

  useEffect(() => {
    setVersion(initialVersion);
    setCustomVersion('');
  }, [initialVersion, open]);

  if (!action) return null;

  const isUpgrade =
    action === ACTIONS.UPGRADE_NETWORK || action === ACTIONS.UPGRADE_GATEWAY;
  const title =
    action === ACTIONS.UPGRADE_GATEWAY
      ? t('activity:networkRollout.VERSION_MODAL_TITLE_GATEWAY_UPGRADE')
      : isUpgrade
      ? t('activity:networkRollout.VERSION_MODAL_TITLE_UPGRADE')
      : t('activity:networkRollout.VERSION_MODAL_TITLE_UPDATE');
  const proceedLabel = isUpgrade
    ? t('activity:networkRollout.VERSION_MODAL_PROCEED_UPGRADE')
    : t('activity:networkRollout.VERSION_MODAL_PROCEED_UPDATE');

  const options: { value: VersionType; label: string }[] = [
    { value: 'stable', label: t('activity:networkRollout.VERSION_STABLE') },
    { value: 'latest', label: t('activity:networkRollout.VERSION_LATEST') },
    { value: 'custom', label: t('activity:networkRollout.VERSION_CUSTOM') }
  ];

  // Handle custom version input change - only allow numeric input up to 10 digits
  const handleCustomVersionChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) && value.length <= 10) {
      setCustomVersion(value);
    }
  };

  // Get validation error message for inline display
  const getCustomVersionError = (): string => {
    if (!customVersion) return '';
    // Numeric validation is handled by handleCustomVersionChange
    if (customVersion.length !== 10) {
      return t('activity:networkRollout.VERSION_CUSTOM_ERROR_LENGTH');
    }
    return '';
  };

  // Show error when custom is selected, has input, and is invalid
  const customVersionError =
    version === 'custom' ? getCustomVersionError() : '';

  // Disable proceed button when custom is selected but invalid
  const isProceedDisabled =
    version === 'custom' && !isValidCustomVersion(customVersion);

  // Handle proceed click
  const handleProceed = () => {
    const versionToSend = version === 'custom' ? customVersion : version;
    isUpgrade ? onProceed(versionToSend) : onProceed(versionToSend, gradual);
  };

  return (
    <Modal
      id="versionSelectionModal"
      open={open}
      dialogTitleProps={{ children: title }}
      dialogContentProps={{
        children: (
          <div className={classes.content} role="radiogroup">
            <div className={classes.label}>
              {t('activity:networkRollout.VERSION_MODAL_SELECT_LABEL')}
            </div>
            {options.map(opt => (
              <Radio
                key={opt.value}
                checked={version === opt.value}
                onChange={() => setVersion(opt.value)}
                value={opt.value}
                name="network-rollout-version"
                formControlLabelProps={{
                  label: opt.label,
                  classes: {
                    root: classes.radio
                  }
                }}
              />
            ))}
            {version === 'custom' && (
              <div className={classes.customInputContainer}>
                <TextField
                  value={customVersion}
                  onChange={handleCustomVersionChange}
                  placeholder={t(
                    'activity:networkRollout.VERSION_CUSTOM_PLACEHOLDER'
                  )}
                  error={!!customVersionError}
                  helperText={customVersionError}
                  fullWidth
                  size="small"
                  className={classes.customInput}
                  inputProps={{
                    'data-testid': 'custom-version-input',
                    maxLength: 10
                  }}
                />
              </div>
            )}
            {!isUpgrade && (
              <Checkbox
                checked={gradual}
                onChange={event => setGradual(event.target.checked)}
                formControlLabelProps={{
                  label: t(
                    'activity:networkRollout.VERSION_MODAL_GRADUAL_LABEL'
                  ),
                  classes: {
                    root: classes.checkbox
                  }
                }}
              />
            )}
          </div>
        )
      }}
      dialogActionsProps={{
        children: (
          <>
            <Button variant="text" color="third" onClick={onClose}>
              {t('activity:buttons.CANCEL')}
            </Button>
            <Button
              color="primary"
              onClick={handleProceed}
              disabled={isProceedDisabled}
              data-testid="version-selection-proceed"
            >
              {proceedLabel}
            </Button>
          </>
        )
      }}
      onClose={onClose}
    />
  );
};

export default VersionSelectionModal;
